<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import SpinnerComponent from '@/modules/shared/components/SpinnerComponent.vue';

const router = useRouter();

const props = defineProps({
    type: {
        type: String,
        default: 'button'
    },
    to: {
        type: String,
        default: ''
    },
    hierarcy: {
        type: String,
        default: 'primary'
    },
    size: {
        type: String,
        default: 'base'
    },
    text: {
        type: String,
        required: true
    },
    number: {
        type: Number,
        default: null
    },
    loadingText: {
        type: String,
        default: ''
    },
    url: {
        type: String,
        default: undefined
    },
    disabled: {
        type: Boolean,
        default: false
    },
    isRequest: {
        type: Boolean,
        default: false
    },
    loading: {
        type: Boolean,
        default: false
    },
    iconLeading: {
        type: [Object, Function],
        default: () => {}
    },
    iconTrailing: {
        type: [Object, Function],
        default: () => {}
    }
});

const isDisabled = computed(() => props.disabled || props.loading);

const setClassButton = computed(() => {
    if (props.hierarcy === 'linkPrimary' || props.hierarcy === 'linkGray' || props.hierarcy === 'linkGrayBlack') return buttonColors[props.hierarcy].button;
    return `${buttonSizes[props.size]} ${buttonColors[props.hierarcy].button}`;
});

// Object.freeze read only, immutable object
const buttonSizes = Object.freeze({
    sm: 'text-sm leading-5 py-1 px-2',
    base: 'text-sm leading-5 py-1.5 px-2.5',
    lg: 'text-sm leading-5 px-4 py-2.5'
});

// Object.freeze read only, immutable object
const buttonColors = Object.freeze({
    primary: {
        text: 'text-white hover:text-white',
        button: 'relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg bg-primary-600 enabled:hover:bg-primary-500 primaryGradient'
    },
    red: {
        text: 'text-white hover:text-white',
        button: 'relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg bg-red-600 enabled:hover:bg-red-500 redGradient'
    },
    gray: {
        text: 'text-white hover:text-white',
        button: 'relative isolate inline-flex items-center justify-center gap-x-2 rounded-lg bg-gray-900 enabled:hover:bg-gray-800 grayGradient'
    },
    outline: {
        text: 'text-gray-950',
        button: 'relative isolate bg-white inline-flex items-center justify-center gap-x-2 enabled:hover:bg-gray-950/[2.5%] shadow-[0_1px_3px_0_rgb(0_0_0_/_0.1),0_1px_2px_-1px_rgb(0_0_0_/_0.1),0px_0px_0px_1px_rgba(0,0,0,0.10)]'
    },
    plain: {
        text: 'text-gray-950',
        button: 'relative isolate inline-flex items-center justify-center gap-x-2 border-transparent enabled:hover:bg-gray-950/5'
    },
    linkGray: {
        text: 'text-gray-500 hover:text-gray-600 px-0 py-0 text-sm leading-5',
        button: 'px-0 py-0 leading-5 gap-1.5'
    },
    linkGrayBlack: {
        text: 'text-gray-950 hover:text-gray-600 px-0 py-0 text-sm leading-5',
        button: 'px-0 py-0 leading-5 gap-1.5'
    },
    linkPrimary: {
        text: 'text-primary-600 hover:text-primary-500 px-0 py-0 text-sm leading-5',
        button: 'px-0 py-0 gap-1.5'
    }
});

const iconColors = Object.freeze({
    primary: 'text-primary-400 enabled:hover:text-primary-300',
    red: 'text-red-300 enabled:hover:text-red-200',
    gray: 'text-gray-400 enabled:hover:text-gray-300',
    outline: 'text-gray-500 enabled:hover:text-gray-700',
    plain: 'text-gray-500 enabled:hover:text-gray-700',
    linkGray: 'text-gray-500 hover:text-gray-700',
    linkPrimary: 'text-primary-400'
});

const handleRoute = () => {
    if (props.to) router.push(props.to);
};

const handleSpinnerColor = () => {
    if (props.hierarcy === 'primary') return 'fill-blue-600';
    else if (props.hierarcy === 'red') return 'fill-red-600';
    return 'fill-gray-500';
};
</script>

<template>
    <component
        :is="props.type"
        data-test="btn"
        :class="`${setClassButton} ${iconColors[props.hierarcy]}`"
        :disabled="isDisabled"
        class="flex items-center gap-1.5 justify-center font-inter font-semibold antialiased transition ease-linear duration-75 enabled:cursor-pointer rounded-lg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500/60 whitespace-nowrap overflow-hidden text-ellipsis"
        @click="handleRoute"
    >
        <div v-if="props.loading && props.isRequest" class="absolute flex items-center gap-2 leading-9">
            <SpinnerComponent :class="`h-4 w-4 ${handleSpinnerColor()}`" />
            <span v-if="props.loadingText">{{ props.loadingText }}</span>
        </div>

        <component :is="iconLeading" v-if="iconLeading && props.loading && props.isRequest" class="h-4 w-4 invisible" data-test="iconLeading"></component>
        <span v-if="props.loading && props.isRequest" class="invisible">{{ props.text }}</span>
        <component :is="iconTrailing" v-if="iconTrailing && props.loading && props.isRequest" class="h-4 w-4 invisible" data-test="iconTrailing"></component>

        <component :is="iconLeading" v-if="iconLeading && !(props.loading && props.isRequest)" class="h-4 w-4" data-test="iconLeading"></component>
        <span v-if="!(props.loading && props.isRequest)" :class="buttonColors[props.hierarcy].text">{{ props.text }}</span>
        <span v-if="props.number" class="h-5 w-5 flex items-center justify-center bg-gray-100 rounded-sm text-center text-gray-500 text-xs leading-tight">{{ props.number }}</span>
        <component :is="iconTrailing" v-if="iconTrailing && !(props.loading && props.isRequest)" class="h-4 w-4" data-test="iconTrailing"></component>
    </component>
</template>

<style lang="css">
.primaryGradient {
    box-shadow:
        0 1px 0 0 var(--shades-base-white-15, rgba(255, 255, 255, 0.15)) inset,
        0 1px 3px 0 var(--shades-base-black-10, rgba(0, 0, 0, 0.1)),
        0 1px 2px -1px var(--shades-base-black-10, rgba(0, 0, 0, 0.1)),
        0 0 0 1px var(--shades-primary-700-90, rgba(31, 73, 214, 0.9));
}

.redGradient {
    box-shadow:
        0 1px 0 0 var(--shades-base-white-15, rgba(255, 255, 255, 0.15)) inset,
        0 1px 3px 0 var(--shades-base-black-10, rgba(0, 0, 0, 0.1)),
        0 1px 2px -1px var(--shades-base-black-10, rgba(0, 0, 0, 0.1)),
        0 0 0 1px var(--shades-red-700-90, rgba(185, 28, 28, 0.9));
}

.grayGradient {
    box-shadow:
        0 1px 0 0 var(--shades-base-white-15, rgba(255, 255, 255, 0.15)) inset,
        0 1px 3px 0 var(--shades-base-black-10, rgba(0, 0, 0, 0.1)),
        0 1px 2px -1px var(--shades-base-black-10, rgba(0, 0, 0, 0.1)),
        0 0 0 1px var(--shades-gray-900-90, rgba(0, 0, 0, 1));
}
</style>
